import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { MdArrowBackIosNew, MdArrowForwardIos } from "react-icons/md";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { gsap } from "gsap";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useSelector } from "react-redux";
import {
  selectGallery,
  selectGalleryError,
} from "../store/slices/gallerySlice";

const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const useMedia = (queries, values, defaultValue) => {
  const get = () =>
    values[queries.findIndex((q) => matchMedia(q).matches)] ?? defaultValue;
  const [value, setValue] = useState(get);

  useEffect(() => {
    const handler = () => setValue(get);
    queries.forEach((q) => matchMedia(q).addEventListener("change", handler));
    return () =>
      queries.forEach((q) =>
        matchMedia(q).removeEventListener("change", handler)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries]);

  return value;
};

const useMeasure = () => {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setSize({ width, height });
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  return [ref, size];
};

const Masonry = ({ items }) => {
  const columns = useMedia(
    [
      "(min-width:1500px)",
      "(min-width:1100px)",
      "(min-width:800px)",
      "(min-width:600px)",
    ],
    [5, 4, 3, 2],
    1
  );

  const [containerRef, { width }] = useMeasure();
  const [imagesReady, setImagesReady] = useState(false);
  const hasMounted = useRef(false);

  useEffect(() => {
    const preload = async () => {
      await Promise.all(
        items.map(
          (i) =>
            new Promise((resolve) => {
              const img = new Image();
              img.src = i.img;
              img.onload = img.onerror = () => resolve();
            })
        )
      );
      setImagesReady(true);
    };
    preload();
  }, [items]);

  const { positionedItems, height } = useMemo(() => {
    if (!width) return { positionedItems: [], height: 0 };
    const gap = 16;
    const colHeights = new Array(columns).fill(0);
    const totalGaps = (columns - 1) * gap;
    const columnWidth = (width - totalGaps) / columns;

    const mapped = items.map((child) => {
      const col = colHeights.indexOf(Math.min(...colHeights));
      const x = col * (columnWidth + gap);
      const height = columnWidth * (child.aspect || 0.75);
      const y = colHeights[col];
      colHeights[col] += height + gap;
      return { ...child, x, y, w: columnWidth, h: height };
    });

    return {
      positionedItems: mapped,
      height: Math.max(...colHeights, 0),
    };
  }, [columns, items, width]);

  useLayoutEffect(() => {
    if (!imagesReady) return;

    positionedItems.forEach((item, index) => {
      const selector = `[data-key="${item.id}"]`;
      const animProps = { x: item.x, y: item.y, width: item.w, height: item.h };

      if (!hasMounted.current) {
        gsap.fromTo(
          selector,
          {
            opacity: 0,
            x: item.x,
            y: item.y + 80,
            width: item.w,
            height: item.h,
            filter: "blur(10px)",
          },
          {
            opacity: 1,
            ...animProps,
            filter: "blur(0px)",
            duration: 0.75,
            ease: "power3.out",
            delay: index * 0.05,
          }
        );
      } else {
        gsap.to(selector, {
          ...animProps,
          duration: 0.6,
          ease: "power3.out",
          overwrite: "auto",
        });
      }
    });

    hasMounted.current = true;
  }, [imagesReady, positionedItems]);

  const handleMouseEnter = (id) => {
    gsap.to(`[data-key="${id}"]`, {
      scale: 0.97,
      duration: 0.25,
      ease: "power2.out",
    });
  };

  const handleMouseLeave = (id) => {
    gsap.to(`[data-key="${id}"]`, {
      scale: 1,
      duration: 0.25,
      ease: "power2.out",
    });
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      style={{ height: height || 400 }}
    >
      {positionedItems.map((item) => (
        <div
          key={item.id}
          data-key={item.id}
          className="absolute box-content will-change-transform"
          style={{ width: item.w, height: item.h }}
          onMouseEnter={() => handleMouseEnter(item.id)}
          onMouseLeave={() => handleMouseLeave(item.id)}
        >
          <div className="relative w-full h-full overflow-hidden rounded-xl shadow-[0px_10px_40px_-16px_rgba(0,0,0,0.35)]">
            <img
              src={item.img}
              alt="Gallery item"
              className="w-full h-full object-cover transition-transform duration-500"
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const Gallery = () => {
  const images = useSelector(selectGallery);
  const error = useSelector(selectGalleryError);

  const [items, setItems] = useState([]);

  useEffect(() => {
    let active = true;
    const buildItems = async () => {
      const loaded = await Promise.all(
        images.map(
          (src, index) =>
            new Promise((resolve) => {
              const img = new Image();
              img.src = src;
              img.onload = () =>
                resolve({
                  id: `img-${index}`,
                  img: src,
                  aspect:
                    img.naturalWidth > 0
                      ? img.naturalHeight / img.naturalWidth
                      : 0.75,
                });
              img.onerror = () =>
                resolve({
                  id: `img-${index}`,
                  img: src,
                  aspect: 0.75,
                });
            })
        )
      );

      if (active) {
        setItems(shuffleArray(loaded));
      }
    };

    if (images && images.length) {
      buildItems();
    }

    return () => {
      active = false;
    };
  }, [images]);

  if (error) {
    return (
      <>
        <h3 className="text-2xl lg:text-4xl text-primary_dark my-4 lg:mt-20 lg:mb-10 text-center h-10 lg:h-12 w-full">
          {error}
        </h3>
      </>
    );
  }

  return (
    <div>
      {/* Carousel */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="relative rounded-2xl overflow-hidden shadow-2xl">
          <Swiper
            modules={[Navigation, Pagination, Autoplay]}
            navigation={{
              prevEl: ".custom-prev",
              nextEl: ".custom-next",
            }}
            pagination={{ clickable: true }}
            autoplay={{ delay: 3000 }}
            loop
            className="w-full h-[280px] sm:h-[360px] lg:h-[480px]"
          >
            {items.map((item) => (
              <SwiperSlide key={item.id}>
                <img
                  src={item.img}
                  alt="Gallery slide"
                  className="w-full h-full object-cover"
                />
              </SwiperSlide>
            ))}
            <div
              className="custom-prev absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-primary cursor-pointer text-3xl sm:text-4xl z-10 
rounded-full w-12 h-12 sm:w-[55px] sm:h-[55px] flex items-center justify-center 
border border-primary bg-secondary/80 backdrop-blur hover:bg-secondary_light transition"
            >
              <MdArrowBackIosNew className="w-6 h-6 sm:w-7 sm:h-7" />
            </div>
            <div
              className="custom-next absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-primary cursor-pointer text-3xl sm:text-4xl z-10 
rounded-full w-12 h-12 sm:w-[55px] sm:h-[55px] flex items-center justify-center 
border border-primary bg-secondary/80 backdrop-blur hover:bg-secondary_light transition"
            >
              <MdArrowForwardIos className="w-6 h-6 sm:w-7 sm:h-7" />
            </div>
          </Swiper>
        </div>
      </div>

      {/* Masonry */}
      <div className="px-4 sm:px-6 lg:px-8 pb-16">
        {items.length ? (
          <Masonry items={items} />
        ) : (
          <div className="text-center text-primary_dark py-12">
            Loading gallery...
          </div>
        )}
      </div>
    </div>
  );
};

export default Gallery;
